import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import {
  usersApi,
  type FileImportPreview,
  type FileImportRow,
  type ImportRow,
  type StudentImport,
} from '@/api/users';
import { coursesApi, type Marathon } from '@/api/courses';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import {
  importErrorText,
  isImportFinished,
  marathonFromQuery,
  sameInputs,
  tomorrowISO,
  type ImportInputs,
} from './importFile';

/**
 * Импорт учеников файлом CSV на марафон (D56, договор students-import.md §8).
 *
 * Четыре шага на одной странице: марафон и срок → файл → пробный прогон →
 * запись с опросом отчёта.
 *
 * ⚠️ «ЗАПИСАТЬ» ТОЛЬКО ПОСЛЕ ПРОБНОГО ПРОГОНА НА ЭТОМ ЖЕ НАБОРЕ. Запись заводит
 * учётки и рассылает письма — сотни за раз. Сменили файл, марафон или дату —
 * проверенное больше не то, что уйдёт, и кнопка снова закрыта.
 *
 * ⚠️ ТЕКСТЫ ОШИБОК — С СЕРВЕРА, ДОСЛОВНО. `reason` строк и `message` отказов уже
 * человеческие; коды здесь не переводятся (договор §2, §7).
 */

const POLL_MS = 2500;

/** Все рабочие курсы: сервер отдаёт не больше 100 за раз, поэтому постранично. */
async function loadAllMarathons(): Promise<Marathon[]> {
  const all: Marathon[] = [];
  for (let page = 0; page < 20; page++) {
    const { items, total } = await coursesApi.listAllMarathons({ limit: 100, offset: all.length, template: 'false' });
    all.push(...(items ?? []));
    if (!items?.length || all.length >= total) break;
  }
  return all;
}

export function AdminStudentImportPage() {
  const { t, i18n } = useTranslation();
  const ask = useConfirm();

  const [marathonId, setMarathonId] = useState('');
  const [accessUntil, setAccessUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<{ inputs: ImportInputs; preview: FileImportPreview } | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateError, setTemplateError] = useState('');
  // ⚠️ Второй клик до перерисовки: `disabled` ещё не успел встать, а запрос уже
  // ушёл. Флаг в ref виден сразу — второй запрос не уходит.
  const busy = useRef(false);

  // Марафон приходит в адресе из панели «Добавить учеников» на курсе
  // («Загрузить файлом»), чтобы не выбирать тот же курс второй раз.
  const [params] = useSearchParams();
  const wanted = params.get('marathon_id');
  const presetDone = useRef(false);

  const marathons = useQuery({
    queryKey: ['admin', 'marathons', 'for-file-import'],
    queryFn: loadAllMarathons,
  });
  const options = (marathons.data ?? []).filter((m) => m.status !== 'archived' && !m.is_template);
  const marathonTitle = options.find((m) => m.id === marathonId)?.title ?? '';

  /**
   * ⚠️ ПОДСТАВЛЯЕМ ОДИН РАЗ И ТОЛЬКО ПОСЛЕ ЗАГРУЗКИ СПИСКА.
   *
   * До загрузки `options` пуст — сверять курс не с чем, и марафон из адреса
   * молча отбросился бы. Повторно применять нельзя: человек мог выбрать другой
   * курс или сбросить выбор, и адрес вернул бы своё поверх его решения.
   */
  useEffect(() => {
    if (presetDone.current || marathons.isPending) return;
    presetDone.current = true;
    const id = marathonFromQuery(wanted, options);
    if (id) setMarathonId((prev) => prev || id);
  }, [marathons.isPending, wanted, options]);

  const inputs: ImportInputs = { file, marathonId, accessUntil };
  const fresh = checked && sameInputs(checked.inputs, inputs) ? checked.preview : null;

  const preview = useMutation({
    mutationFn: (snap: ImportInputs) =>
      usersApi.previewImportFile({ file: snap.file as File, marathonId: snap.marathonId, accessUntil: snap.accessUntil }),
    onSuccess: (data, snap) => setChecked({ inputs: snap, preview: data }),
  });
  const start = useMutation({
    mutationFn: (snap: ImportInputs) =>
      usersApi.startImportFile({ file: snap.file as File, marathonId: snap.marathonId, accessUntil: snap.accessUntil }),
    onSuccess: (rec) => setImportId(rec.id),
  });

  const report = useQuery({
    queryKey: ['admin', 'imports', importId],
    queryFn: () => usersApi.getImport(importId as string),
    enabled: !!importId,
    refetchInterval: (q) => (isImportFinished(q.state.data?.import.status) ? false : POLL_MS),
    // ⚠️ И в скрытой вкладке. 400 новичков — минуты; админ уходит в почту, а без
    // этого опрос встаёт до возврата, и «идёт запись» висит, когда всё давно готово.
    refetchIntervalInBackground: true,
  });

  const running = preview.isPending || start.isPending;
  const toWrite = fresh ? fresh.counts.create + fresh.counts.enroll : 0;
  const canCheck = !!file && !!marathonId && !running && !importId;
  const canWrite = !!fresh && toWrite > 0 && !running && !importId;

  const onCheck = async () => {
    if (busy.current || !canCheck) return;
    busy.current = true;
    start.reset();
    try {
      await preview.mutateAsync({ ...inputs });
    } catch {
      // Текст отказа рисуется из preview.error.
    } finally {
      busy.current = false;
    }
  };

  const onWrite = async () => {
    if (busy.current || !canWrite || !checked) return;
    busy.current = true;
    try {
      const yes = await ask({
        title: t('adminStudentImport.confirmTitle'),
        message: t('adminStudentImport.confirmMessage', { count: toWrite, course: marathonTitle }),
        confirmLabel: t('adminStudentImport.write'),
      });
      if (!yes) return;
      await start.mutateAsync({ ...checked.inputs });
    } catch {
      // Текст отказа рисуется из start.error.
    } finally {
      busy.current = false;
    }
  };

  const downloadTemplate = async () => {
    if (templateBusy) return;
    setTemplateBusy(true);
    setTemplateError('');
    try {
      const blob = await usersApi.downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shablon-uchenikov.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setTemplateError(importErrorText(e));
    } finally {
      setTemplateBusy(false);
    }
  };

  const startOver = () => {
    setFile(null);
    setChecked(null);
    setImportId(null);
    preview.reset();
    start.reset();
  };

  const error = start.error ?? preview.error;
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title={t('adminStudentImport.title')} subtitle={t('adminStudentImport.subtitle')} />

      {/* Шаг 1 */}
      <Card>
        <CardTitle>{t('adminStudentImport.step1')}</CardTitle>
        <div className="mt-3 space-y-3">
          <Select
            label={t('adminStudentImport.marathonLabel')}
            placeholder={marathons.isLoading ? t('common.loading') : t('adminStudentImport.marathonPlaceholder')}
            options={options.map((m) => ({ value: m.id, label: m.title }))}
            value={marathonId}
            disabled={!!importId}
            onChange={(e) => setMarathonId(e.target.value)}
            data-testid="import-marathon"
          />
          <div>
            <label htmlFor="import-until" className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('adminStudentImport.untilLabel')}
            </label>
            <input
              id="import-until"
              type="date"
              min={tomorrowISO()}
              value={accessUntil}
              disabled={!!importId}
              onChange={(e) => setAccessUntil(e.target.value)}
              className="h-12 w-full rounded-button bg-surface-container-lowest px-4 text-on-surface shadow-soft"
            />
            <p className="mt-1 text-xs text-on-surface-variant">{t('adminStudentImport.untilHint')}</p>
          </div>
        </div>
      </Card>

      {/* Шаг 2 */}
      <Card>
        <CardTitle>{t('adminStudentImport.step2')}</CardTitle>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label
            className={`inline-flex cursor-pointer items-center gap-2 rounded-pill border border-outline-variant px-4 py-2 text-sm font-semibold ${
              importId ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">upload_file</span>
            {t('adminStudentImport.chooseFile')}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="import-file"
              disabled={!!importId}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                // Сбрасываем поле, иначе тот же файл после правки в Excel повторно не выберется.
                e.target.value = '';
              }}
            />
          </label>
          <Button type="button" variant="ghost" loading={templateBusy} onClick={downloadTemplate}>
            <span className="material-symbols-outlined text-[18px]">download</span>
            {t('adminStudentImport.template')}
          </Button>
        </div>
        {file && <p className="mt-2 break-all text-sm font-medium">{file.name}</p>}
        <p className="mt-2 text-xs text-on-surface-variant">{t('adminStudentImport.excelHint')}</p>
        {templateError && <p className="mt-2 text-sm text-error">{templateError}</p>}
      </Card>

      {/* Шаг 3 */}
      <Card>
        <CardTitle>{t('adminStudentImport.step3')}</CardTitle>
        <p className="mt-1 text-sm text-on-surface-variant">{t('adminStudentImport.checkHint')}</p>
        <div className="mt-3">
          <Button type="button" disabled={!canCheck} loading={preview.isPending} onClick={onCheck} data-testid="import-check">
            {t('adminStudentImport.check')}
          </Button>
        </div>
        {checked && !fresh && !importId && (
          <p className="mt-3 text-sm text-on-surface-variant" data-testid="import-stale">
            {t('adminStudentImport.stale')}
          </p>
        )}
        {fresh && <PreviewView preview={fresh} fmtDate={fmtDate} />}
      </Card>

      {/* Шаг 4 */}
      <Card>
        <CardTitle>{t('adminStudentImport.step4')}</CardTitle>
        {!importId && (
          <>
            <p className="mt-1 text-sm text-on-surface-variant">
              {fresh && toWrite === 0 ? t('adminStudentImport.nothingToWrite') : t('adminStudentImport.writeHint')}
            </p>
            <div className="mt-3">
              <Button type="button" disabled={!canWrite} loading={start.isPending} onClick={onWrite} data-testid="import-write">
                {t('adminStudentImport.write')}
              </Button>
            </div>
          </>
        )}
        {importId && (
          <ReportView
            imp={report.data?.import}
            rows={report.data?.rows ?? []}
            loadError={report.error ? importErrorText(report.error) : ''}
            onStartOver={startOver}
          />
        )}
      </Card>

      {error && (
        <p className="rounded-card bg-error/5 p-3 text-sm text-error" role="alert" data-testid="import-error">
          {importErrorText(error)}
        </p>
      )}
    </div>
  );
}

function PreviewView({ preview, fmtDate }: { preview: FileImportPreview; fmtDate: (iso: string) => string }) {
  const { t } = useTranslation();
  const rows = preview.rows ?? [];
  const errors = rows.filter((r) => r.action === 'error');
  const notes = rows.filter((r) => r.action !== 'error' && r.note);
  const before = preview.same_file_before ?? [];
  const c = preview.counts;

  return (
    <div className="mt-4 space-y-3" data-testid="import-preview">
      {before.length > 0 && (
        <div className="flex gap-2 rounded-card bg-warning-container p-3 text-sm text-on-surface" data-testid="import-same-file">
          <span className="material-symbols-outlined text-[18px]">info</span>
          <span>{t('adminStudentImport.sameFileBefore', { dates: before.map((b) => fmtDate(b.created_at)).join(', ') })}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Num label={t('adminStudentImport.countCreate')} value={c.create} accent testId="count-create" />
        <Num label={t('adminStudentImport.countEnroll')} value={c.create + c.enroll} accent testId="count-enroll" />
        <Num label={t('adminStudentImport.countAlready')} value={c.already} testId="count-already" />
        <Num label={t('adminStudentImport.countErrors')} value={c.errors} danger={c.errors > 0} testId="count-errors" />
      </div>
      <p className="text-xs text-on-surface-variant">
        {preview.access_until
          ? t('adminStudentImport.accessUntil', { date: preview.access_until })
          : preview.duration_days > 0
            ? t('adminStudentImport.accessDays', { count: preview.duration_days })
            : t('adminStudentImport.accessByCourse')}
      </p>

      {errors.length > 0 && (
        <RowsTable title={t('adminStudentImport.errorsTitle')} rows={errors} text={(r) => r.reason ?? ''} tone="text-error" testId="import-error-rows" />
      )}
      {notes.length > 0 && (
        <RowsTable title={t('adminStudentImport.notesTitle')} rows={notes} text={(r) => r.note ?? ''} tone="text-on-surface-variant" testId="import-note-rows" />
      )}
    </div>
  );
}

function RowsTable<R extends FileImportRow | ImportRow>({
  title,
  rows,
  text,
  tone,
  testId,
}: {
  title: string;
  rows: R[];
  text: (r: R) => string;
  tone: string;
  testId: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mb-1 text-sm font-semibold">{title}</p>
      <div className="max-h-80 overflow-y-auto rounded-card border border-outline-variant" data-testid={testId}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-outline-variant text-left text-xs text-on-surface-variant">
              <th className="w-14 p-2 text-right">{t('adminStudentImport.colLine')}</th>
              <th className="p-2">{t('adminStudentImport.colRow')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.line} className="border-b border-outline-variant/60 align-top last:border-0">
                <td className="p-2 text-right text-xs tabular-nums text-on-surface-variant">{r.line}</td>
                <td className="p-2">
                  <span className="break-all">{r.email || r.full_name || '—'}</span>
                  <span className={`block text-xs ${tone}`}>{text(r)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportView({
  imp,
  rows,
  loadError,
  onStartOver,
}: {
  imp?: StudentImport;
  rows: ImportRow[];
  loadError: string;
  onStartOver: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const done = isImportFinished(imp?.status);
  const failedRows = rows.filter((r) => r.action === 'failed' || r.state === 'failed');

  const downloadPasswords = async () => {
    if (!imp || saving) return;
    setSaving(true);
    try {
      const blob = await usersApi.downloadImportPasswords(imp.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onebilim-dostupy-${imp.created_at.slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-3" data-testid="import-report">
      {!done && (
        <p className="text-sm text-on-surface-variant" data-testid="import-running">
          {t('adminStudentImport.running')}
        </p>
      )}
      {loadError && <p className="text-sm text-error">{loadError}</p>}

      {imp && done && (
        <>
          <p className={`text-sm font-semibold ${imp.status === 'failed' ? 'text-error' : 'text-primary'}`} data-testid="import-done">
            {imp.status === 'failed' ? t('adminStudentImport.finishedFailed') : t('adminStudentImport.finishedDone')}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Num label={t('adminStudentImport.resCreated')} value={imp.created} accent testId="res-created" />
            <Num label={t('adminStudentImport.resEnrolled')} value={imp.enrolled} accent testId="res-enrolled" />
            <Num label={t('adminStudentImport.resAlready')} value={imp.already_enrolled ?? 0} testId="res-already" />
            <Num label={t('adminStudentImport.resErrors')} value={imp.errors ?? 0} testId="res-errors" />
            <Num label={t('adminStudentImport.resFailed')} value={imp.failed} danger={imp.failed > 0} testId="res-failed" />
          </div>
          {imp.error && <p className="rounded-card bg-error/5 p-3 text-sm text-error">{imp.error}</p>}
          {failedRows.length > 0 && (
            <RowsTable title={t('adminStudentImport.failedTitle')} rows={failedRows} text={(r) => r.reason ?? ''} tone="text-error" testId="import-failed-rows" />
          )}
        </>
      )}

      <div className="flex flex-wrap gap-2">
        <Link
          to="/admin/imports"
          className="inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-on-primary"
          data-testid="import-journal-link"
        >
          <span className="material-symbols-outlined text-[18px]">list_alt</span>
          {t('adminStudentImport.journal')}
        </Link>
        {imp && done && imp.created > 0 && (
          <Button type="button" variant="ghost" loading={saving} onClick={downloadPasswords}>
            <span className="material-symbols-outlined text-[18px]">download</span>
            {t('imports.downloadPasswords')}
          </Button>
        )}
        {done && (
          <>
            <Link to="/admin/groups" className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-primary">
              {t('adminStudentImport.groups')}
            </Link>
            <Button type="button" variant="ghost" onClick={onStartOver}>
              {t('adminStudentImport.another')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Num({
  label,
  value,
  accent,
  danger,
  testId,
}: {
  label: string;
  value: number;
  accent?: boolean;
  danger?: boolean;
  testId?: string;
}) {
  const box = danger
    ? 'border-error bg-error/5'
    : accent
      ? 'border-primary bg-primary/5'
      : 'border-outline-variant bg-surface-variant/30';
  const num = danger ? 'text-error' : accent ? 'text-primary' : '';
  return (
    <div className={`rounded-card border p-3 ${box}`}>
      <p className={`text-2xl font-bold tabular-nums ${num}`} data-testid={testId}>
        {value}
      </p>
      <p className="text-xs text-on-surface-variant">{label}</p>
    </div>
  );
}
